import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { autocompleteKeys } from '@/api/keys';

// Types
export interface SerializedSuggestion {
  placeId: string;
  displayText: string;
  mainText: string;
  secondaryText: string;
  types: string[];
}

// Utility functions
const processAutocompleteSuggestions = (
  suggestions: google.maps.places.AutocompleteSuggestion[]
): SerializedSuggestion[] => {
  return suggestions.map((suggestion) => ({
    placeId: suggestion.placePrediction?.placeId || '',
    displayText: suggestion.placePrediction?.text?.text || '',
    mainText: suggestion.placePrediction?.mainText?.text || '',
    secondaryText: suggestion.placePrediction?.secondaryText?.text || '',
    types: suggestion.placePrediction?.types || [],
  }));
};

const createAutocompleteRequest = (
  inputValue: string,
  sessionToken: google.maps.places.AutocompleteSessionToken,
  includedPrimaryTypes: string[]
): google.maps.places.AutocompleteRequest => ({
  input: inputValue.trim(),
  sessionToken,
  // Only include types filter if array is not empty
  ...(includedPrimaryTypes.length > 0 && { includedPrimaryTypes }),
});

// Configuration for different autocomplete types
// Filter to cities and regions only (no landmarks, businesses, or POIs)
const LOCATION_PRIMARY_TYPES: string[] = [
  'locality',                      // Cities/towns
  'administrative_area_level_1',   // States/provinces
  'administrative_area_level_2',   // Counties
  'postal_code',                   // Postal codes (for flexibility)
];
// Google Places (New) API caps includedPrimaryTypes at 5 entries and uses
// cuisine-specific primary types (e.g. 'thai_restaurant') rather than the generic
// 'restaurant' type — meaning any fixed list will miss most ethnic cuisines.
// We omit the filter entirely and let the query text surface relevant results.
const RESTAURANT_PRIMARY_TYPES: string[] = [];

/**
 * Internal hook for fetching Google Places suggestions
 * Uses TanStack Query for caching and request deduplication
 */
function useAutocompleteSuggestions(
  inputValue: string,
  sessionToken: google.maps.places.AutocompleteSessionToken | null,
  includedPrimaryTypes: string[],
  type: 'location' | 'restaurant',
  enabled: boolean
) {
  return useQuery({
    queryKey:
      type === 'location'
        ? autocompleteKeys.locationSuggestions({ inputValue })
        : autocompleteKeys.restaurantSuggestions({ inputValue }),
    queryFn: async (): Promise<SerializedSuggestion[]> => {
      if (!sessionToken || !inputValue.trim()) {
        return [];
      }

      const request = createAutocompleteRequest(
        inputValue,
        sessionToken,
        includedPrimaryTypes
      );

      const { suggestions } =
        await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
          request
        );

      return processAutocompleteSuggestions(suggestions);
    },
    enabled: enabled && !!inputValue.trim() && !!sessionToken,
    staleTime: 5 * 60 * 1000, // 5 minutes - suggestions don't change frequently
    gcTime: 10 * 60 * 1000, // 10 minutes cache
  });
}

/**
 * Hook for location autocomplete (regions, cities, etc.)
 * Returns suggestions, search state, and control functions
 */
export function useLocationAutocomplete() {
  const queryClient = useQueryClient();
  const [places, setPlaces] = useState<google.maps.PlacesLibrary>();
  const [searchValue, setSearchValue] = useState('');
  const [debouncedValue, setDebouncedValue] = useState('');
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const initialized = !!places && !!sessionTokenRef.current;

  // Load Google Maps Places library
  useEffect(() => {
    const loadPlacesLibrary = async () => {
      try {
        const placesLibrary = (await google.maps.importLibrary(
          'places'
        )) as google.maps.PlacesLibrary;
        setPlaces(placesLibrary);
      } catch (error) {
        console.error('Failed to load Google Maps Places library:', error);
      }
    };
    loadPlacesLibrary();
  }, []);

  // Initialize session token
  useEffect(() => {
    if (places && !sessionTokenRef.current) {
      sessionTokenRef.current = new places.AutocompleteSessionToken();
    }
  }, [places]);

  // Debounce search value
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(searchValue);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchValue]);

  // Fetch suggestions
  const {
    data: suggestions = [],
    isLoading: loading,
    error,
  } = useAutocompleteSuggestions(
    debouncedValue,
    sessionTokenRef.current,
    LOCATION_PRIMARY_TYPES,
    'location',
    initialized
  );

  const fetchSuggestions = useCallback((inputValue: string) => {
    setSearchValue(inputValue);
  }, []);

  const selectSuggestion = useCallback(
    (suggestion: SerializedSuggestion) => {
      setSearchValue(suggestion.displayText);
      setDebouncedValue(suggestion.displayText);
      // Reset session token after selection
      if (places) {
        sessionTokenRef.current = new places.AutocompleteSessionToken();
      }
      // Clear the suggestions cache for this search
      queryClient.removeQueries({
        queryKey: autocompleteKeys.location(),
      });
      return suggestion;
    },
    [places, queryClient]
  );

  const clearSearch = useCallback(() => {
    setSearchValue('');
    setDebouncedValue('');
    if (places) {
      sessionTokenRef.current = new places.AutocompleteSessionToken();
    }
    queryClient.removeQueries({
      queryKey: autocompleteKeys.location(),
    });
  }, [places, queryClient]);

  const resetSearch = useCallback(() => {
    clearSearch();
  }, [clearSearch]);

  return {
    suggestions,
    searchValue,
    loading,
    error: error?.message || null,
    initialized,
    fetchSuggestions,
    selectSuggestion,
    clearSearch,
    resetSearch,
    setSearchValue,
  };
}

/**
 * Hook for restaurant autocomplete
 * Returns suggestions, search state, and control functions
 */
export function useRestaurantAutocomplete() {
  const queryClient = useQueryClient();
  const [places, setPlaces] = useState<google.maps.PlacesLibrary>();
  const [searchValue, setSearchValue] = useState('');
  const [debouncedValue, setDebouncedValue] = useState('');
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const initialized = !!places && !!sessionTokenRef.current;

  // Load Google Maps Places library
  useEffect(() => {
    const loadPlacesLibrary = async () => {
      try {
        const placesLibrary = (await google.maps.importLibrary(
          'places'
        )) as google.maps.PlacesLibrary;
        setPlaces(placesLibrary);
      } catch (error) {
        console.error('Failed to load Google Maps Places library:', error);
      }
    };
    loadPlacesLibrary();
  }, []);

  // Initialize session token
  useEffect(() => {
    if (places && !sessionTokenRef.current) {
      sessionTokenRef.current = new places.AutocompleteSessionToken();
    }
  }, [places]);

  // Debounce search value
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(searchValue);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchValue]);

  // Fetch suggestions
  const {
    data: suggestions = [],
    isLoading: loading,
    error,
  } = useAutocompleteSuggestions(
    debouncedValue,
    sessionTokenRef.current,
    RESTAURANT_PRIMARY_TYPES,
    'restaurant',
    initialized
  );

  const fetchSuggestions = useCallback((inputValue: string) => {
    setSearchValue(inputValue);
  }, []);

  const selectSuggestion = useCallback(
    (suggestion: SerializedSuggestion) => {
      setSearchValue(suggestion.displayText);
      // Reset session token after selection
      if (places) {
        sessionTokenRef.current = new places.AutocompleteSessionToken();
      }
      // Clear the suggestions cache for this search
      queryClient.removeQueries({
        queryKey: autocompleteKeys.restaurant(),
      });
      return suggestion;
    },
    [places, queryClient]
  );

  const clearSearch = useCallback(() => {
    setSearchValue('');
    setDebouncedValue('');
    if (places) {
      sessionTokenRef.current = new places.AutocompleteSessionToken();
    }
    queryClient.removeQueries({
      queryKey: autocompleteKeys.restaurant(),
    });
  }, [places, queryClient]);

  const resetSearch = useCallback(() => {
    clearSearch();
  }, [clearSearch]);

  return {
    suggestions,
    searchValue,
    loading,
    error: error?.message || null,
    initialized,
    fetchSuggestions,
    selectSuggestion,
    clearSearch,
    resetSearch,
    setSearchValue,
  };
}
